import React from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetOverlay } from '@frontend/shadcn/components/ui/sheet';
import { CalendarIcon, XmarkIcon, PencilIcon, CheckIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import dayjs from 'dayjs';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { type ContentPreviewData, type ContentPreviewSheetProps as BaseContentPreviewSheetProps } from './types';
import { PlatformIcon, PostTypeLabel } from './components/content-preview-header';
import {
  CreatorAvatar,
  CreatorInfo,
  CreatorActionDownload,
  CreatorActionShare,
  CreatorActionsContainer,
  CreatorContainer,
  CreatorAvatarContainer,
} from './components/content-preview-creator';
import ContentPreviewContent from './components/content-preview-content';
import { MetricCard, MetricCardLabel, MetricCardValue } from './components/metric-card';
import { PerformanceHeader, PerformanceTitle, PerformanceDateRange } from './components/performance-header';
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  PreviewSheetHeading,
  PreviewSheetCaption,
  PreviewSheetLabel,
  PreviewSheetSection,
  PreviewSheetBadgeContainer,
  PreviewSheetBadge,
  PreviewSheetEmpty,
  MetricsGrid,
  SheetHeader,
  SheetTitle,
} from './components/content-preview-sheet';

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface ContentPreviewSheetProps extends Omit<BaseContentPreviewSheetProps, 'content'> {
  content: ContentPreviewData & {
    onEditProject?: () => void;
    onEditGroup?: () => void;
    onEditMusic?: () => void;
    onEditRetailer?: () => void;
    onEditBrands?: () => void;
  };
}

export function ContentPreviewSheet({ content, open, onOpenChange, availableProjects, availableGroups, children }: ContentPreviewSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden"
        side="right"
      >
        <div className="flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Content Preview</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" aria-label="Close preview" onClick={() => onOpenChange(false)}>
                <XmarkIcon className="h-4 w-4" />
              </Button>
            </SheetClose>
          </SheetHeader>

          <SheetTopBar>
            <div className="flex items-center gap-2">
              <PlatformIcon platform={content.metadata.platform} />
              <PostTypeLabel postType={content.metadata.postType} />
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-2 rounded-lg">
                <CalendarIcon className="h-3 w-3" />
                <span>
                  {new Date(content.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </Badge>
            </div>
          </SheetTopBar>

          <SheetGrid>
            {/* Left column - Creator, Content and Caption */}
            <SheetColumn>
              <PreviewSheetContent>
                <CreatorContainer>
                  <CreatorAvatarContainer>
                    <CreatorAvatar name={content.creator.name} avatarUrl={content.creator.avatarUrl} />
                    <CreatorInfo name={content.creator.name} handle={content.creator.handle} network={content.creator.network} />
                  </CreatorAvatarContainer>
                  <CreatorActionsContainer>
                    <CreatorActionDownload onClick={content.onDownload} />
                    <CreatorActionShare
                      onClick={() => {
                        if (content.onShare) {
                          content.onShare();
                        } else {
                          window.open(window.location.href, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    />
                  </CreatorActionsContainer>
                </CreatorContainer>

                <ContentPreviewContent
                  mediaUrl={content.mediaUrl}
                  contentType={content.metadata.contentType}
                  originalUrl={content.originalUrl}
                />

                <div>
                  <PreviewSheetHeading>Post Caption</PreviewSheetHeading>
                  <PreviewSheetCaption caption={content.caption} />
                </div>
              </PreviewSheetContent>
            </SheetColumn>

            {/* Right column - Tabs, Metrics and Details */}
            <SheetColumn>
              <Tabs defaultValue="overview" className="w-full">
                <div className="border-b mt-3">
                  <TabsList className="border-0 bg-transparent m-0">
                    <TabsTrigger
                      value="overview"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Overview
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="overview" className="mt-4">
                  <Accordion
                    type="multiple"
                    defaultValue={['performance', 'info', 'additional']}
                    className="space-y-4"
                  >
                    <AccordionItem value="performance" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <PerformanceHeader className="w-full">
                          <PerformanceTitle>Post Performance</PerformanceTitle>
                          <PerformanceDateRange>
                            {content.metrics.metadata.startDate && content.metrics.metadata.endDate && (
                            <>
                              {dayjs(content.metrics.metadata.startDate).format('MMM D, YYYY')} -{' '}
                              {dayjs(content.metrics.metadata.endDate).format('MMM D, YYYY')}
                            </>
                              )}
                          </PerformanceDateRange>
                        </PerformanceHeader>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <MetricsGrid>
                          <MetricCard className="border-[#4F46E5]">
                            <MetricCardLabel>Impressions</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.impressions)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#E11D48]">
                            <MetricCardLabel>Engagement</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.engagement)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#EC4899]">
                            <MetricCardLabel>Engagement %</MetricCardLabel>
                            <MetricCardValue>{content.metrics.engagementRate.toFixed(1)}%</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#059669]">
                            <MetricCardLabel>TMV</MetricCardLabel>
                            <MetricCardValue>{currencyFormatter.format(content.metrics.tmv ?? 0)}</MetricCardValue>
                          </MetricCard>
                        </MetricsGrid>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="info" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <h3 className="text-md font-medium">Info</h3>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="space-y-4">
                          <PreviewSheetSection>
                            <PreviewSheetLabel>Project</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.projectEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.projectEditingState.projectId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableProjects) {
                                        const selectedProject = availableProjects.find((p) => p.id.toString() === value);
                                        if (selectedProject && content.projectEditingState) {
                                          // Just update the state, don't save yet
                                          content.projectEditingState.updateState?.({
                                            projectId: selectedProject.id.toString(),
                                            projectName: selectedProject.name
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a project" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {availableProjects?.map((project) => (
                                        <SelectItem key={project.id} value={project.id.toString()}>
                                          {project.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.projectEditingState?.onSave &&
                                          content.projectEditingState.projectId &&
                                          content.projectEditingState.projectName) {
                                        content.projectEditingState.onSave(
                                          content.projectEditingState.projectId,
                                          content.projectEditingState.projectName
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.projectEditingState?.onCancel) {
                                        content.projectEditingState.onCancel();
                                      }
                                    }}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.project ? (
                                    <PreviewSheetBadge className="bg-blue-500 hover:bg-blue-600">
                                      {content.metadata.project.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditProject && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={content.onEditProject}>
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Group</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.groupEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.groupEditingState.groupId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableGroups) {
                                        const selectedGroup = availableGroups.find((g) => g.id.toString() === value);
                                        if (selectedGroup && content.groupEditingState) {
                                          // Just update the state, don't save yet
                                          content.groupEditingState.updateState?.({
                                            groupId: selectedGroup.id.toString(),
                                            groupName: selectedGroup.name
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a group" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {availableGroups?.map((group) => (
                                        <SelectItem key={group.id} value={group.id.toString()}>
                                          {group.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.groupEditingState?.onSave &&
                                          content.groupEditingState.groupId &&
                                          content.groupEditingState.groupName) {
                                        content.groupEditingState.onSave(
                                          content.groupEditingState.groupId,
                                          content.groupEditingState.groupName
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={content.groupEditingState?.onCancel}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.group ? (
                                    <PreviewSheetBadge className="bg-pink-500 hover:bg-pink-600">
                                      {content.metadata.group.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditGroup && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={content.onEditGroup}>
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Last Updated Date</PreviewSheetLabel>
                            <div className="text-sm">
                              {new Date(content.updatedAt || content.createdAt).toLocaleDateString()}
                            </div>
                          </PreviewSheetSection>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>
              </Tabs>
            </SheetColumn>
          </SheetGrid>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ContentPreviewSheet;
